import React, { useState } from 'react';
import { useAuth } from '../../auth';
import { extractTime } from './dateTimeUtils';
import { validateWithinEventWindow, equalsTimeDeclared, TimeRuleContext } from './timeValidation';
import { processNoteText } from '../../utils/processNoteText';

/**
 * Note entry type.
 */
interface NoteEntry {
  id: string;
  date: string;
  time: string;
  alias: string;
  defectIds: number[];
  text: string;
}

/**
 * Props for the NotesSection component.
 */
interface NotesSectionProps {
  noteEntries: NoteEntry[];
  setNoteEntries: React.Dispatch<React.SetStateAction<NoteEntry[]>>;
  defectsCount: number;
  mode: 'view' | 'edit' | 'create';
  /** Whether at least one note is required for this record (gates Create). */
  notesRequired?: boolean;
  /** The event's Time Declared — seeds the first note and bounds note times. */
  timeDeclaredZulu?: string;
  /** The event's ERTS — bounds note times once the event is Completed. */
  returnToServiceTimeZulu?: string;
  /** Whether the event status is Completed (enforces the ERTS ceiling rule). */
  isCompleted?: boolean;
  /**
   * When true, notes may be dated before the Time Declared (Deferred records).
   * The not-before-Time-Declared restriction is lifted; the ERTS ceiling still
   * applies when Completed (Deferred records carry no ERTS, so it never fires).
   */
  allowNotesBeforeTimeDeclared?: boolean;
}

/**
 * Notes list section with add-note popup.
 */
const NotesSection: React.FC<NotesSectionProps> = ({
  noteEntries,
  setNoteEntries,
  defectsCount,
  mode,
  notesRequired,
  timeDeclaredZulu,
  returnToServiceTimeZulu,
  isCompleted,
  allowNotesBeforeTimeDeclared,
}) => {
  const { user } = useAuth();
  const currentAlias = user ? user.email.split('@')[0] : '';
  const [isAddingNote, setIsAddingNote] = useState<boolean>(false);
  const [newNoteDate, setNewNoteDate] = useState<string>('');
  const [newNoteTime, setNewNoteTime] = useState<string>('');
  const [newNoteDefectIds] = useState<number[]>([]);
  const [newNoteText, setNewNoteText] = useState<string>('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState<string>('');
  const [notesExpanded, setNotesExpanded] = useState<boolean>(false);

  const isEditable = mode === 'create' || mode === 'edit';

  const handleOpenAddNote = (): void => {
    // Rule 2: the first note auto-populates its date/time to match the event's
    // Time Declared. Subsequent notes default to the current UTC time.
    const declaredDate = timeDeclaredZulu ? timeDeclaredZulu.split(/[ T]/)[0] : '';
    const declaredTime = extractTime(timeDeclaredZulu || '');
    if (noteEntries.length === 0 && declaredDate && declaredTime) {
      setNewNoteDate(declaredDate);
      setNewNoteTime(declaredTime);
      setNewNoteText('');
      setIsAddingNote(true);
      return;
    }
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = String(now.getUTCMonth() + 1).padStart(2, '0');
    const d = String(now.getUTCDate()).padStart(2, '0');
    setNewNoteDate(`${y}-${m}-${d}`);
    setNewNoteTime(
      `${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}`,
    );
    setNewNoteText('');
    setIsAddingNote(true);
  };

  // Cross-field temporal rules for the in-progress note: its date/time must be
  // later than Time Declared (rule 1) and not after the ERTS once Completed
  // (rule 4). Only evaluated once a full HH:MM time has been entered.
  const noteTimeContext: TimeRuleContext = {
    timeDeclared: allowNotesBeforeTimeDeclared ? undefined : timeDeclaredZulu,
    erts: returnToServiceTimeZulu,
    isCompleted: !!isCompleted,
  };
  // A new note may match the Time Declared only when no existing note already
  // does — at most one note is allowed to equal the Time Declared.
  const aNoteAlreadyMatchesTimeDeclared = noteEntries.some((note) =>
    equalsTimeDeclared(`${note.date} ${note.time}`, timeDeclaredZulu),
  );
  const noteTimeError =
    newNoteDate && newNoteTime && newNoteTime.length >= 4
      ? validateWithinEventWindow(`${newNoteDate} ${newNoteTime}`, noteTimeContext, {
          allowEqualToTimeDeclared: !aNoteAlreadyMatchesTimeDeclared,
        })
      : null;

  // Notes are shown newest-first. When more than five exist, only the five most
  // recent are shown until the user expands the section via the arrow.
  const NOTES_COLLAPSED_LIMIT = 5;
  const sortedNotes = [...noteEntries].sort((a, b) =>
    `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`),
  );
  const hasCollapsibleNotes = noteEntries.length > NOTES_COLLAPSED_LIMIT;
  const visibleNotes =
    hasCollapsibleNotes && !notesExpanded
      ? sortedNotes.slice(0, NOTES_COLLAPSED_LIMIT)
      : sortedNotes;

  return (
    <>
      <div className="field-row field-row-tall">
        <label className="field-label">
          {hasCollapsibleNotes && (
            <button
              className="notes-collapse-toggle"
              type="button"
              onClick={() => setNotesExpanded((expanded) => !expanded)}
              aria-label={notesExpanded ? 'Show only the 5 most recent notes' : 'Show all notes'}
              aria-expanded={notesExpanded}
              title={notesExpanded ? 'Collapse to 5 most recent' : 'Show all notes'}
            >
              <span className={`toggle-arrow ${notesExpanded ? 'open' : ''}`}>&#9656;</span>
            </button>
          )}
          Notes
          {isEditable && (
            <button
              className={`defect-add-btn${notesRequired && noteEntries.length === 0 ? ' required-action-missing' : ''}`}
              type="button"
              disabled={defectsCount === 0}
              onClick={handleOpenAddNote}
              aria-label="Add note"
            >
              +
            </button>
          )}
        </label>
        <div className="field-group defect-group">
          {visibleNotes.map((note) => (
            <div key={note.id} className="note-entry">
              <div className="note-entry-header">
                <span className="note-timestamp">
                  {note.date} {note.time}z
                </span>
                <span className="note-alias">({note.alias})</span>
                {isEditable && (
                  <button
                    className="defect-edit-btn"
                    type="button"
                    aria-label="Edit note"
                    onClick={() => {
                      setEditingNoteId(note.id);
                      setEditNoteText(note.text);
                    }}
                  >
                    &#9998;
                  </button>
                )}
                {isEditable && (
                  <button
                    className="defect-delete-btn"
                    type="button"
                    aria-label="Delete note"
                    onClick={() => {
                      setNoteEntries((prev) => prev.filter((n) => n.id !== note.id));
                      if (editingNoteId === note.id) setEditingNoteId(null);
                    }}
                  >
                    &#128465;
                  </button>
                )}
              </div>
              {editingNoteId === note.id ? (
                <div className="defect-edit-row">
                  <textarea
                    className="field-textarea"
                    value={editNoteText}
                    onChange={(e) => setEditNoteText(e.target.value)}
                    rows={3}
                  />
                  <button
                    className="defect-save-btn"
                    type="button"
                    onClick={() => {
                      const processedText = processNoteText(editNoteText);
                      setNoteEntries((prev) =>
                        prev.map((n) => (n.id === note.id ? { ...n, text: processedText } : n)),
                      );
                      setEditingNoteId(null);
                    }}
                  >
                    Save
                  </button>
                  <button
                    className="defect-cancel-btn"
                    type="button"
                    onClick={() => setEditingNoteId(null)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="note-entry-text">{note.text}</div>
              )}
            </div>
          ))}
          {noteEntries.length === 0 && !isAddingNote && (
            <span className="defect-empty">No notes recorded</span>
          )}
        </div>
      </div>

      {isAddingNote && (
        <div className="barrier-popup-overlay">
          <div className="barrier-popup" onClick={(e) => e.stopPropagation()}>
            <div className="barrier-popup-header">
              <h3>New Note</h3>
              <button className="modal-close" type="button" onClick={() => setIsAddingNote(false)}>
                &times;
              </button>
            </div>
            <div className="barrier-popup-body">
              <div className="barrier-popup-field">
                <label>Date *</label>
                <input
                  type="date"
                  className="field-input"
                  value={newNoteDate}
                  onChange={(e) => setNewNoteDate(e.target.value)}
                />
              </div>
              <div className="barrier-popup-field">
                <label>Time (Zulu) *</label>
                <input
                  type="text"
                  className="field-input"
                  value={newNoteTime}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^0-9]/g, '');
                    if (val.length > 4) val = val.substring(0, 4);
                    if (val.length >= 3) val = val.substring(0, 2) + ':' + val.substring(2);
                    setNewNoteTime(val);
                  }}
                  placeholder="HH:MM"
                  maxLength={5}
                />
              </div>
              <div className="barrier-popup-field">
                <label>Created By</label>
                <input
                  type="text"
                  className="field-input"
                  value={currentAlias || '—'}
                  readOnly
                  disabled
                />
              </div>
              <div className="barrier-popup-field">
                <label>Note *</label>
                <textarea
                  className="field-textarea"
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  rows={4}
                  placeholder="Enter note text"
                />
              </div>
            </div>
            <div className="barrier-popup-footer">
              {noteTimeError && (
                <span className="field-inline-error" role="alert">
                  {noteTimeError}
                </span>
              )}
              <button
                className="modal-btn modal-btn-cancel"
                type="button"
                onClick={() => setIsAddingNote(false)}
              >
                Cancel
              </button>
              <button
                className="modal-btn modal-btn-save"
                type="button"
                disabled={!newNoteDate || !newNoteTime || !newNoteText.trim() || !!noteTimeError}
                onClick={() => {
                  const processedText = processNoteText(newNoteText.trim());
                  setNoteEntries((prev) => [
                    ...prev,
                    {
                      id: `note-${Date.now()}`,
                      date: newNoteDate,
                      time: newNoteTime,
                      alias: currentAlias || '—',
                      defectIds: newNoteDefectIds,
                      text: processedText,
                    },
                  ]);
                  setIsAddingNote(false);
                }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotesSection;
